import { type ChangeEvent, type MouseEvent, useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate } from "react-router-dom";

import ErrorToast from "@/app/components/ErrorToast";
import type { GroupedSkill } from "@/app/components/ResumeBuilder/SkillsEditorListItems/SkillsEditorListItems";
import Spinner from "@/app/components/Spinner";
import { Button } from "@/app/components/ui/button";
import { Checkbox } from "@/app/components/ui/checkbox";
import { Label } from "@/app/components/ui/label";
import type { ResumeContextValue } from "@/app/types/resume";
import tips from "../../../../assets/copy/tips";
import AutoSuggestion from "../../../../components/AutoSuggestion";
import SectionActions from "../../../../components/ResumeBuilder/SectionActions";
import SkipSectionDialog from "../../../../components/ResumeBuilder/SkipSectionDialog";
import Tips from "../../../../components/Tips";
import InputField from "../../../../components/UIComponents/InputField";
import SelectField from "../../../../components/UIComponents/SelectField";
import ResumeContext from "../../../../context/ResumeContext";
import { NavigationHandler } from "../../../../handlers/navigationHandler";
import {
	convertArrayToUl,
	convertSplittedSkillsToNormalSkillsArray,
	convertUlToArray,
	dateToMYObject,
	type ListItem,
	stripSkillGrouping,
} from "../../../../handlers/utilityFunctions";
import { resumeObjectToContextFormat } from "../../../../handlers/valueTransformation";
import { nextAfterWorkHistory, useEducationFirst } from "../../../../router/sectionOrder";
import { firstFieldErrors, workExperienceSchema } from "../../../../schemas/resume";
import { StaticValue } from "../../../../seed/constants";
import options from "../../../../seed/dropDownOptions";
import mapping from "../../../../seed/metadata/filemapping";
import { usePatchResumeSection } from "../../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../../services/create-resume/resumeSections";
import RelevantSkillsPicker from "../components/RelevantSkillsPicker";
import WorkDetailsEditor from "../components/WorkDetailsEditor";

interface WorkHistoryFormState {
	referenceId: string;
	jobTitle: string;
	employer: string;
	startDate: string;
	endDate: string;
	currentEmployer: boolean | "";
}

interface WorkHistoryErrors {
	jobTitle?: string;
	employer?: string;
	address?: string;
	country?: string;
	startDate?: string;
	endDate?: string;
}

/** An empty editor still carries the paragraph Quill starts with. */
const isEditorEmpty = (value: string) => value === "" || value === "<p><br></p>";

const ResumeWorkHistory = () => {
	const navigate = useNavigate();

	const navigationHandler = new NavigationHandler();
	const link = useLocation().pathname.split("/")[2];
	const [cookies] = useCookies(["resume"]);
	const staticValues = new StaticValue().getDefaultValues();
	const patchResumeSection = usePatchResumeSection();

	const locationState = useLocation().state as { referenceId?: string } | null;
	const referenceId = locationState && locationState.referenceId;
	const locations: string[] = mapping["districtsRegions"][0].districtRegions;
	const [country, setCountry] = useState("Tanzania");

	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;

	const { journal } = resumeData;
	const resumeCurrentStatus = journal ? journal.currentStatus : false;
	const isComplete = resumeCurrentStatus == "COMPLETED";

	//Declaring the form states
	const [workHistory, setWorkHistory] = useState<WorkHistoryFormState>({
		referenceId: "",
		jobTitle: "",
		employer: "",
		startDate: "",
		endDate: "",
		currentEmployer: "",
	});

	const [isInputCompleted, setIsInputCompleted] = useState(false);
	const [workLocation, setWorkLocation] = useState("");
	const [showSkipDialog, setShowSkipDialog] = useState(false);
	const [serverErrors, setServerErrors] = useState<string[]>([]);

	// The achievements editor and the skills it applied used to sit behind a
	// dialog of their own. They are the rest of one job, so they are asked for on
	// the same screen as the job.
	const [workDetails, setWorkDetails] = useState("");
	const [workDetailsError, setWorkDetailsError] = useState("");
	const [groupedSkills, setGroupedSkills] = useState<GroupedSkill[]>([]);
	const [customSkills, setCustomSkills] = useState("");

	const updateWorkDetails = (value: string) => {
		setWorkDetailsError("");
		setWorkDetails(value);
	};

	//The form event handler
	const onChangeInput = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		if (e.target.name === "workLocation") {
			setWorkLocation(e.target.value);
		} else if (e.target.name === "country") {
			setCountry(e.target.value);
		} else {
			setWorkHistory((prevState) => {
				const capitalized = e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1);
				return {
					...prevState,
					[e.target.name]: capitalized,
				};
			});
		}
	};

	const [errors, setErrors] = useState<WorkHistoryErrors>({});

	/** The saved skills string split back into its categories and its loose entries. */
	function hydrateSkills(saved: string) {
		// NOTE: custom entries intentionally have no `bp` flag — the save then
		// serialises them as `<p>...</p>`, exactly like the legacy page did.
		const customEntries: ListItem[] = [];
		const categories = convertUlToArray(saved)
			.map((skillObject) => {
				const [beforeColon, afterColon] = skillObject.value.split(/:(.+)/);
				const title = beforeColon.trim();

				if (afterColon === undefined) {
					customEntries.push({ value: title } as ListItem);
					return undefined;
				}

				return {
					title,
					// See the matching parse in ResumeSkils: splitting on "," leaves a
					// leading space that the rebuild's ", " join then doubles, and the
					// source data's grouping braces are not for the reader.
					skills: afterColon
						.trim()
						.split(",")
						.map((skillItem) => ({ added: true, skill: stripSkillGrouping(skillItem) })),
					collapsed: true,
				};
			})
			.filter((item): item is GroupedSkill => item !== undefined);

		setGroupedSkills(categories);
		setCustomSkills(convertArrayToUl(customEntries));
	}

	useEffect(() => {
		if (resumeData.workHistory.length > 0 && resumeData.workHistory[0].jobTitle !== "" && referenceId) {
			const data = resumeData.workHistory.filter((item) => item.referenceId === referenceId)[0];
			if (!data) return;

			// Compute + set both pieces of state directly — never call one setter
			// from inside another's updater (updaters must be pure and can replay
			// under StrictMode / concurrent rendering).
			//
			// The country has its own field, so it is kept out of this box. Reading it
			// back in put the country in the region slot on the next save.
			setWorkLocation([...new Set([data.city, data.region].filter(Boolean))].join(", "));
			setCountry(data.country || "Tanzania");
			setWorkHistory({
				referenceId: data.referenceId && data.referenceId,
				jobTitle: data.jobTitle,
				employer: data.employer,
				startDate: data.startDate,
				endDate: data.endDate,
				currentEmployer: data.currentEmployer,
			});
			setWorkDetails(data.details);
			hydrateSkills(data.relevantSkills || "");
		}
	}, [resumeData, referenceId]);

	useEffect(() => {
		const address = `${workLocation}, ${country}`;
		const validation = workExperienceSchema.safeParse({ ...workHistory, address, country });

		setIsInputCompleted(validation.success && !isEditorEmpty(workDetails));
	}, [workHistory, workLocation, country, workDetails]);

	function onSuccess(ledger: unknown) {
		resumeObjectToContextFormat(ledger, setResumeData);
		navigationHandler.getNext(navigationHandler.template.navigationFlow, link, isComplete);
		navigate("/resume/work-history-summary");
	}

	function onError(error: Error) {
		const serverResponse = (error as unknown as { response: { data: { message: string | string[] } } }).response.data;
		setServerErrors((previous) => previous.concat(serverResponse.message));
	}

	//form submission handler
	const saveWorkHistory = (e: MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		setServerErrors([]);

		const validation = workExperienceSchema.safeParse({
			...workHistory,
			address: `${workLocation}, ${country}`,
			country,
		});
		const hasWorkDetails = !isEditorEmpty(workDetails);

		setErrors(validation.success ? {} : (firstFieldErrors(validation.error) as WorkHistoryErrors));
		setWorkDetailsError(hasWorkDetails ? "" : "Please enter work details");

		if (!validation.success || !hasWorkDetails) return;

		// Either part alone is accepted, but the backend calls a section complete
		// only when city, region and country are all filled. A single part therefore
		// goes into both slots rather than leaving a blank that would keep this
		// section unfinished with nothing on screen to explain it.
		const [first = "", second = ""] = workLocation.split(",").map((part) => part.trim());

		const data: Record<string, unknown> = {
			referenceId: workHistory.referenceId,
			jobtitle: workHistory.jobTitle,
			employer: workHistory.employer,
			employerLocation: {
				city: first || second,
				region: second || first,
				country,
			},
			startDate: dateToMYObject(workHistory.startDate),
			endDate: workHistory.endDate === "" ? null : dateToMYObject(workHistory.endDate),
			currentEmployer: workHistory.endDate === "",
			reponsibilitiesAndAccomplishements: convertUlToArray(workDetails),
			// Drop blanks. The custom-skills editor contributes an empty paragraph
			// when it has never been typed in, which was stored as a `{value: ""}`
			// entry against the job. The Skills screen already filters these out.
			relevantSkills: convertSplittedSkillsToNormalSkillsArray(groupedSkills)
				.concat(convertUlToArray(customSkills))
				.filter((skill) => skill.value !== ""),
		};

		const resumeId = cookies[staticValues.localStorageResumeId];
		patchResumeSection.mutate({ resumeId, section: RESUME_SECTIONS.EXPERIENCE, data } as never, { onSuccess, onError });
	};

	const educationFirst = useEducationFirst();
	const sectionCounts = {
		educationCount: resumeData.education.length,
		workHistoryCount: resumeData.workHistory.length,
	};

	// Skills for a reader with no experience, who was asked for education before this
	// screen; Education for everyone else, who has not reached it yet.
	const skipDestination = nextAfterWorkHistory(educationFirst, sectionCounts);

	const skipSection = () => {
		navigationHandler.getNext(navigationHandler.template.navigationFlow, link, isComplete);
		navigate(`/resume/${skipDestination}`);
	};

	return (
		<>
			<SkipSectionDialog open={showSkipDialog} onOpenChange={setShowSkipDialog} onSkip={skipSection} />

			<div className="min-h-screen">
				{/* Server-side failures only. Field-level problems are reported inline
				    on the field that caused them, never here. */}
				{serverErrors.map((item, index) => {
					return <ErrorToast error={item} key={index} />;
				})}
				<div className="mb-6">
					<h2 className="mb-3 !font-bold !text-2xl text-gray-900 !md:text-3xl">Tell us about your most recent job </h2>
					<div className="flex w-full items-start justify-between gap-3">
						<p className="text-gray-600">
							We&#39;ll start there and work backward. <i>No jobs yet? Use Skip for now.</i>
						</p>
						<Tips content={tips.workHistory} />
					</div>
				</div>
				<div className="w-full">
					<form action="">
						<div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
							<div className="mb-4">
								<InputField
									name="jobTitle"
									type="text"
									label="Job Title"
									placeholder="Enter Job Title"
									value={workHistory.jobTitle}
									onChange={onChangeInput}
									error={errors.jobTitle}
								/>
							</div>
							<div className="mb-4">
								<InputField
									type="text"
									label="Employer"
									name="employer"
									value={workHistory.employer}
									onChange={onChangeInput}
									error={errors.employer}
									placeholder="Enter Employer Name"
								/>
							</div>

							<div className="mb-4">
								<SelectField
									name="country"
									value={country}
									onChange={onChangeInput}
									options={options.phoneCountryCodeOptions.map(({ name }: { name: string }) => ({
										value: name,
										name,
									}))}
									label="Country"
									error={errors.country}
								/>
							</div>

							<div className="mb-4">
								{!(country === "Tanzania") && (
									<InputField
										type="text"
										label="Ex: City, Region"
										name="workLocation"
										value={workLocation}
										onChange={onChangeInput}
										placeholder="Enter Work Location"
										error={errors.address}
									/>
								)}
								{country === "Tanzania" && (
									<AutoSuggestion
										suggestions={locations}
										label="Eg: District, Region"
										type="text"
										name="workLocation"
										userInput={workLocation}
										setUserInput={setWorkLocation}
										isForm={true}
										error={errors.address}
									/>
								)}
							</div>

							<div className="mb-4">
								<InputField
									type="date"
									label="Start Date"
									name="startDate"
									value={workHistory.startDate}
									onChange={onChangeInput}
									error={errors.startDate}
								/>
							</div>
							<div className="mb-4">
								{!workHistory.currentEmployer && (
									<InputField
										type="date"
										label="End Date"
										name="endDate"
										disabled={!!workHistory.currentEmployer}
										value={workHistory.endDate}
										onChange={onChangeInput}
										error={errors.endDate}
									/>
								)}
							</div>
							{/* A plain div, not a label: `Label` renders its own <label>, and nesting
							    one inside another is invalid markup that swallowed clicks on the
							    caption. `htmlFor` keeps the caption clickable. */}
							<div className="mb-3 flex items-center gap-3 md:col-span-2">
								<Checkbox
									id="customCheck"
									name="currentEmployer"
									checked={!!workHistory.currentEmployer}
									onCheckedChange={() => {
										setWorkHistory((prevState) => {
											return {
												...prevState,
												endDate: "",
												currentEmployer: !prevState.currentEmployer,
											};
										});
									}}
								/>
								<Label className="cursor-pointer" htmlFor="customCheck">
									I still work here
								</Label>
							</div>

							{/* `md:` matters: an unprefixed span of 2 opens a second, implicit column
							    on the one-column phone grid, which the fields then flow across two at
							    a time — each pair overlapping in a track sized to nothing. */}
							<div className="mb-4 md:col-span-2">
								<WorkDetailsEditor
									jobTitle={workHistory.jobTitle}
									value={workDetails}
									setValue={updateWorkDetails}
									error={workDetailsError}
								/>
							</div>

							<div className="mb-4 md:col-span-2">
								<RelevantSkillsPicker
									jobTitle={workHistory.jobTitle}
									employer={workHistory.employer}
									groupedSkills={groupedSkills}
									setGroupedSkills={setGroupedSkills}
									customSkills={customSkills}
									setCustomSkills={setCustomSkills}
								/>
							</div>
						</div>
						{/* No back button: "Skip for now" already walks the reader forward past
						    this section, and a third choice beside it only asked them to pick a
						    direction they had not asked to change. */}
						<SectionActions className="flex items-center justify-end gap-3">
							<div className="flex items-center gap-3">
								{/* Forward to Skills, the section after this one — matching `skipSection`
								    above. This used to go to education-information, which was the next
								    section only while work history came first; it now walks backwards. */}
								<Button variant="ghost" type="button" onClick={skipSection}>
									Skip for now
								</Button>
								<Button
									type="button"
									onClick={(e) => {
										if (!isInputCompleted) {
											setShowSkipDialog(true);
										}
										saveWorkHistory(e);
									}}
								>
									{patchResumeSection.isPending ? (
										<>
											Loading <Spinner />
										</>
									) : (
										"Continue"
									)}
								</Button>
							</div>
						</SectionActions>
					</form>
				</div>
			</div>
		</>
	);
};

export default ResumeWorkHistory;
